import styled from '@emotion/styled';
import { Page } from '../page';

const TosPageContent = styled.div`
    font-family: "Work Sans", sans-serif;
    padding: 2rem;
    color: #e8e8e8;
    overflow-y: auto;
    flex-grow: 1;
    
    /* Custom scrollbar styling */
    &::-webkit-scrollbar {
        width: 8px;
    }
    
    &::-webkit-scrollbar-track {
        background: rgba(255, 255, 255, 0.05);
        border-radius: 4px;
    }
    
    &::-webkit-scrollbar-thumb {
        background: rgba(255, 0, 0, 0.3);
        border-radius: 4px;
        
        &:hover {
            background: rgba(255, 0, 0, 0.5);
        }
    }

    max-width: 900px;
    margin: 0 auto;
    
    h1 {
        font-size: 2rem;
        font-weight: 700;
        color: #ff4444;
        margin-bottom: 0.5rem;
        text-align: center;
    }
    
    .last-updated {
        text-align: center;
        color: #999;
        font-size: 0.9rem;
        margin-bottom: 2rem;
        font-style: italic;
    }
    
    h2 {
        font-size: 1.4rem;
        font-weight: 600;
        color: #ff6666;
        margin-top: 2rem;
        margin-bottom: 1rem;
        padding-bottom: 0.5rem;
        border-bottom: 2px solid rgba(255, 68, 68, 0.3);
    }
    
    h3 {
        font-size: 1.1rem;
        font-weight: 600;
        color: #ff8888;
        margin-top: 1.5rem;
        margin-bottom: 0.75rem;
    }
    
    p {
        line-height: 1.8;
        margin-bottom: 1rem;
        color: #e8e8e8;
        font-size: 1rem;
    }
    
    ul {
        margin: 1rem 0;
        padding-left: 2rem;
        
        li {
            line-height: 1.8;
            margin-bottom: 0.75rem;
            color: #e8e8e8;
            
            &::marker {
                color: #ff4444;
            }
        }
    }
    
    .highlight {
        background: rgba(255, 68, 68, 0.15);
        border-left: 3px solid #ff4444;
        padding: 1rem 1.25rem;
        border-radius: 0.5rem;
        margin: 1.5rem 0;
        font-weight: 500;
        color: #ffcccc;
    }
    
    .important {
        background: rgba(255, 200, 0, 0.15);
        border-left: 3px solid #ffc800;
        padding: 1rem 1.25rem;
        border-radius: 0.5rem;
        margin: 1.5rem 0;
        font-weight: 500;
        color: #ffe4a0;
    }
    
    strong {
        color: #ff6666;
        font-weight: 600;
    }
`;

export default function TosPage() {
    return (
        <Page id="tos">
            <TosPageContent>
                <h1>Terms of Service</h1>
                <div className="last-updated">Last Updated: October 17, 2025</div>
                
                <p>
                    Welcome to BasedGPT. By accessing or using our service, you agree to be bound by these Terms of Service. 
                    Please read them carefully. If you do not agree to these terms, you may not use our service.
                </p>

                <h2>1. Service Overview</h2>
                <p>
                    BasedGPT provides an AI-powered chat interface and related services. We are committed to providing 
                    a free speech platform while maintaining a safe and legal environment for all users.
                </p>

                <h2>2. Acceptable Use Policy</h2>
                <p>You agree to use our service responsibly and in compliance with all applicable laws. The following activities are strictly prohibited:</p>
                
                <h3>2.1 Prohibited Content</h3>
                <ul>
                    <li><strong>Child Sexual Abuse Material (CSAM):</strong> Any content involving minors in sexual contexts is absolutely prohibited and will result in immediate account termination and reporting to authorities.</li>
                    <li><strong>Sexual Content:</strong> Creation, sharing, or requesting sexual content of any kind is not permitted on this platform. This is the only category of content that will be actively censored.</li>
                </ul>

                <h3>2.2 Logged But Not Censored Content</h3>
                <div className="important">
                    <strong>Important:</strong> We do NOT moderate or censor speech on our platform beyond sexual content. 
                    However, certain types of content will be flagged and logged for legal compliance purposes.
                </div>
                <p>
                    The following content types will be <strong>logged</strong> (along with your IP address and account information) 
                    but will <strong>NOT be censored or blocked</strong>:
                </p>
                <ul>
                    <li><strong>Illegal Activities:</strong> Discussion of illegal activities will be logged but not censored. Messages about illegal subjects, planning illegal actions, or utilizing this service for illegal purposes will be recorded for potential legal compliance. We do not facilitate illegal activity, but we will not censor discussion of it.</li>
                    <li><strong>Violence:</strong> We condemn all forms of violence including religious, political, racial, or any other type of violence. While we do not support content that incites, glorifies, or plans violence, we will <strong>log but not censor</strong> such content. Our logging serves as a record for legal purposes while maintaining our free speech principles.</li>
                </ul>
                <p>
                    <strong>To be clear:</strong> These messages will appear and function normally. We simply maintain records 
                    of flagged content for legal compliance and platform safety purposes.
                </p>

                <h3>2.3 Platform Abuse</h3>
                <ul>
                    <li><strong>Rate Limit Evasion:</strong> Using VPNs, proxies, or other methods to evade rate limits is prohibited.</li>
                    <li><strong>Multiple Accounts:</strong> Creating multiple accounts to circumvent rate limits or account restrictions is not allowed.</li>
                    <li><strong>API Misuse:</strong> Using our backend API for your own service, commercial purposes, or any unauthorized third-party application is strictly prohibited.</li>
                    <li><strong>System Abuse:</strong> Attempting to compromise, disrupt, or gain unauthorized access to our systems or other users' accounts is prohibited.</li>
                </ul>

                <h2>3. Our Values & Commitments</h2>
                <div className="important">
                    <strong>Free Speech Platform:</strong> We are committed to free speech principles. No speech will be censored 
                    beyond sexual content and content that violates applicable laws. We believe in open dialogue and diverse perspectives.
                </div>
                
                <p>However, free speech comes with responsibility:</p>
                <ul>
                    <li>We do not support or condone political extremism of any kind.</li>
                    <li>We condemn all forms of violence, whether religious, political, racial, or otherwise.</li>
                    <li>We do not incite, encourage, or facilitate hatred or harm.</li>
                </ul>

                <h2>4. Privacy & Data Collection</h2>
                <div className="highlight">
                    <strong>Your Privacy Matters:</strong> All messages are private and are NOT logged or stored for review purposes.
                </div>
                
                <p>However, please note:</p>
                <ul>
                    <li><strong>Moderation Flagged Content:</strong> Messages that are flagged by our automated moderation systems ARE logged together with your IP address and any associated account information.</li>
                    <li><strong>Purpose:</strong> This logging is necessary to prevent abuse, ensure platform safety, and comply with legal obligations.</li>
                    <li><strong>Retention:</strong> Flagged content logs may be retained for investigative and legal purposes.</li>
                </ul>

                <h2>5. Account Termination</h2>
                <p>We reserve the right to:</p>
                <ul>
                    <li>Suspend or terminate your access to the service at any time, for any reason, without prior notice.</li>
                    <li>Remove content that violates these Terms of Service.</li>
                    <li>Report illegal activity to appropriate law enforcement authorities.</li>
                    <li>Take legal action against users who violate these terms or applicable laws.</li>
                </ul>

                <h2>6. Rate Limits & Service Availability</h2>
                <p>
                    We implement rate limits to ensure fair access to our service for all users. These limits are subject 
                    to change without notice. We do not guarantee uninterrupted access to our service and reserve the right 
                    to modify, suspend, or discontinue any aspect of the service at any time.
                </p>

                <h2>7. Intellectual Property</h2>
                <p>
                    All content, trademarks, and data on this platform, including but not limited to software, databases, 
                    text, graphics, icons, and hyperlinks, are the property of BasedGPT or its licensors. You may not 
                    reproduce, distribute, or create derivative works without explicit permission.
                </p>

                <h2>8. Disclaimer of Warranties</h2>
                <p>
                    THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED. 
                    We do not guarantee the accuracy, completeness, or usefulness of any information provided through 
                    the service. AI-generated content may contain errors or inaccuracies.
                </p>

                <h2>9. Limitation of Liability</h2>
                <p>
                    TO THE FULLEST EXTENT PERMITTED BY LAW, BasedGPT SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, 
                    SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER INCURRED 
                    DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES.
                </p>

                <h2>10. User Responsibility</h2>
                <p>You are solely responsible for:</p>
                <ul>
                    <li>Your use of the service and any content you create or share.</li>
                    <li>Maintaining the confidentiality of your account credentials.</li>
                    <li>All activities that occur under your account.</li>
                    <li>Ensuring your use complies with applicable laws in your jurisdiction.</li>
                </ul>

                <h2>11. Age Requirement</h2>
                <p>
                    You must be at least 13 years of age to use this service. If you are under 18, you must have 
                    parental or guardian consent to use the service. By using the service, you represent that you 
                    meet these age requirements.
                </p>

                <h2>12. Changes to Terms</h2>
                <p>
                    We reserve the right to modify these Terms of Service at any time. Changes will be effective 
                    immediately upon posting. Your continued use of the service after changes are posted constitutes 
                    acceptance of the modified terms.
                </p>

                <h2>13. Governing Law</h2>
                <p>
                    These Terms of Service shall be governed by and construed in accordance with applicable laws, 
                    without regard to conflict of law principles. Any disputes arising from these terms or your use 
                    of the service shall be resolved in the appropriate courts.
                </p>

                <h2>14. Contact</h2>
                <p>
                    If you have questions about these Terms of Service, please contact us through our official 
                    communication channels listed on our website.
                </p>

                <div className="highlight">
                    <strong>Important:</strong> By using BasedGPT, you acknowledge that you have read, understood, 
                    and agree to be bound by these Terms of Service. Violation of these terms may result in 
                    immediate termination of your access and potential legal action.
                </div>
            </TosPageContent>
        </Page>
    );
}

